import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Workbook, WorkbookInstance } from '@fortune-sheet/react';
import type { Op } from '@fortune-sheet/core';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import { useIsMobileOrTabletDevice } from '../_custom/Hooks/useIsMobileOrTabletDevice';
import { useCloseOnBack } from '../_custom/Hooks/useCloseOnBack';
import CompactDownloadButton from '../DocumentViewer/components/CompactDownloadButton/CompactDownloadButton';
import { useSheetEditorDrawerLogic, SavingPhase } from './useSheetEditorDrawerLogic';
import { FortuneSheet } from './types';
import { SheetStorageRef } from './sheetStorage';
import { MIN_ROW_HEIGHT } from './fileTypes';
import { useColumnAutofit } from './useColumnAutofit';
import { useSheetTabWheelScroll } from './useSheetTabWheelScroll';
import { useWorkbookResizeSync } from './useWorkbookResizeSync';
import { ensureSheetFontList } from './fortuneSheetSetup';
import { useOutsideClick } from '../../utils/constants/hooks';
import { editIconPurple, commentsIcon, ClockCounterDark } from '../../assets/hostedassets';
import RenameReportDialog from '../../modules/Reports/components/RenameReportDialog/RenameReportDialog';
import WhiteCheckIcon from '../Reports/WhiteCheckIcon';
import HistorySideDrawer from '../HistorySideDrawer/HistorySideDrawer';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { resetAndHideCommentsThread, closeCommentsThread, setShowCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../store/commentsThread/commentsThread_reducer';
import styles from './SheetEditorDrawer.module.scss';
import '@fortune-sheet/react/dist/index.css';
import './fortuneSheetOverrides.css';

const HISTORY_PORTAL_TARGET_ID = 'sheet-editor-history';
const COMMENTS_PORTAL_TARGET_ID = 'reports-editor-comments';

// Extend the toolbar font list once, before any <Workbook> mounts.
ensureSheetFontList();

export interface SheetEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Original file name; used as the drawer title and download base name. */
    fileName: string;
    /** In-memory sheets (import / composed). When provided, renders directly — skips URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — carried through so a new version
     *  saved from this drawer groups with its siblings (mirrors the Plate
     *  editor's persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context. When absent (e.g. the read-only DataRoom viewer) the Save
     *  action is hidden and the drawer is view + download only. */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** S3 storage ref from a previously saved document — enables version continuity. */
    storage?: SheetStorageRef;
    /** The opened document's status ('DRAFT' | 'READY') — a draft-preferred open
     *  hands the drawer a live autosave draft, which Save & Close must still
     *  commit even when this session made no edits. */
    status?: string;
    /** Called with the saved document after a successful save. */
    onSaved?: (doc: any) => void;
    /** Called with the saved document after a name/description edit succeeds.
     *  Unlike `onSaved` this does not close the drawer — only the caller's own
     *  list (e.g. the Reports table) needs refreshing; the drawer's header
     *  already reflects the new name. */
    onRenamed?: (doc: any) => void;
    /** Snapshot this sheet into Reports (Ask AICA / Data Room view contexts).
     *  When provided, this becomes the PRIMARY header action instead of Save —
     *  a view context never has both (you don't add a report to reports). */
    onAddToReports?: () => void;
    /** Loading state for the Add to Reports action, controlled by the caller. */
    addingToReports?: boolean;
    /** Briefly true right after a successful add — swaps the button to a
     *  checkmark + "Added" state, controlled by the caller. */
    addedToReports?: boolean;
    /** True while the caller is still preparing the sheets to hand in (e.g.
     *  parsing an imported file) — shows the "Preparing spreadsheet…" loader
     *  instead of resolving `sheets`/`fileUrl` against an empty/undefined value. */
    preparing?: boolean;
    /** Read-only preview: no toolbar, no editing, no persistence, no Add to Workspace. */
    viewOnly?: boolean;
}

const SAVE_PHASE_LABELS: Record<SavingPhase, string> = {
    idle: 'Save and Close',
    serializing: 'Preparing…',
    uploading: 'Uploading…',
    saving: 'Saving…',
};

// Fortune-Sheet's default toolbar with the one-click currency button removed.
// Currency formatting is still reachable via the "format" dropdown; we drop the
// quick button per product (no multi-currency need). currency="₹" makes the
// Format dropdown default to the Indian Rupee instead of the library default (¥).
const TOOLBAR_ITEMS = [
    'undo',
    'redo',
    'format-painter',
    'clear-format',
    '|',
    'percentage-format',
    'number-decrease',
    'number-increase',
    'format',
    '|',
    'font',
    '|',
    'font-size',
    '|',
    'bold',
    'italic',
    'strike-through',
    'underline',
    '|',
    'font-color',
    'background',
    'border',
    'merge-cell',
    '|',
    'horizontal-align',
    'vertical-align',
    'text-wrap',
    'text-rotation',
    '|',
    'freeze',
    'conditionFormat',
    'filter',
    'link',
    'image',
    'comment',
    'quick-formula',
    'dataVerification',
    'splitColumn',
    'locationCondition',
    'screenshot',
    'search',
];

interface SheetWorkbookProps {
    data: FortuneSheet[];
    workbookRef: React.RefObject<WorkbookInstance>;
    customToolbarItems: React.ComponentProps<typeof Workbook>['customToolbarItems'];
    allowEdit: boolean;
    showToolbar: boolean;
    onOp: (ops: Op[]) => void;
}

/**
 * The <Workbook> subtree, isolated behind React.memo and given ONLY the props
 * it actually needs. Fortune-Sheet's `mergedSettings` memo does a full
 * `cloneDeep` + re-runs its mount/init effect on ANY new prop identity — so
 * header/pane state (downloadMenuOpen, renameOpen, autosaveStatus, history/
 * comments toggles, name edits) must never reach this component, or every one
 * of those unrelated re-renders would re-init the whole grid.
 */
const SheetWorkbook = memo(function SheetWorkbook({
    data,
    workbookRef,
    customToolbarItems,
    allowEdit,
    showToolbar,
    onOp,
}: SheetWorkbookProps) {
    return (
        <Workbook
            ref={workbookRef}
            data={data}
            toolbarItems={TOOLBAR_ITEMS}
            customToolbarItems={customToolbarItems}
            lang="en"
            currency="₹"
            showToolbar={showToolbar}
            showFormulaBar
            showSheetTabs
            allowEdit={allowEdit}
            defaultRowHeight={MIN_ROW_HEIGHT}
            onOp={onOp}
        />
    );
});

/**
 * Editable spreadsheet drawer. Accepts either in-memory FortuneSheet[] (from
 * import or compose) or a presigned URL to fetch. Renders a Fortune-Sheet
 * grid (formulas + formula bar built in) inside the same BottomDrawer the
 * PlateEditor uses. Header actions: one "Download ▾" dropdown (.xlsx/.csv),
 * plus a primary action — "Add to Reports" when `onAddToReports` is passed
 * (view contexts), else "Save" when `canSave` (Reports edit/import).
 */
const SheetEditorDrawer: React.FC<SheetEditorDrawerProps> = ({
    open,
    onClose,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    storage,
    status,
    onSaved,
    onRenamed,
    onAddToReports,
    addingToReports,
    addedToReports,
    preparing,
    viewOnly = false,
}) => {
    const isMobile = useIsMobile();
    const isMobileOrTablet = useIsMobileOrTabletDevice();
    useCloseOnBack(open && isMobileOrTablet, onClose);
    const persistenceOrgId = viewOnly ? undefined : assesseeOrgId;

    const {
        sheets,
        workbookSheets,
        loading,
        loadingStage,
        error,
        saving,
        savingPhase,
        autosaveStatus,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        markWorkbookDirty,
        currentName,
        currentDescription,
        handleRename,
        hasGenId,
        historyOpen,
        historyExpanded,
        historyLoading,
        historyEntries,
        historyPreviewLoading,
        toggleHistoryDrawer,
        closeHistoryDrawer,
        toggleHistoryExpand,
        previewHistoryEntry,
        selectHistoryEntry,
        previewEntryId,
        previewActive,
        restoreVersion,
    } = useSheetEditorDrawerLogic({
        open,
        fileName,
        sheets: inMemorySheets,
        fileUrl,
        mimeType,
        docId,
        genId,
        assesseeOrgId: persistenceOrgId,
        investorOrgId,
        createdBy,
        documentType,
        name,
        description,
        storage,
        status,
        onSaved,
        onRenamed,
    });

    // Save is only available with a persistence context (Reports). The read-only
    // DataRoom viewer passes no assesseeOrgId → view + download only. The header's
    // "Edit name & description" affordance shares this same gate.
    const canSave = Boolean(persistenceOrgId);

    const dispatch = useAppDispatch();
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    // Scoped to THIS drawer's source — another surface's open thread must not
    // light this button or slide open an empty pane here (mirrors PlateDocumentDrawer).
    const commentsActive = Boolean(
        canSave &&
            commentsThreadState?.showCommentsThread &&
            !commentsThreadState?.closingCommentsThread &&
            commentsThreadState?.source === COMMENTS_THREAD_SOURCES.REPORTS,
    );
    const commentsExpanded = commentsActive && Boolean(commentsThreadState?.expandCommentsThread);

    const toggleComments = useCallback(() => {
        if (commentsActive) {
            dispatch(closeCommentsThread());
            return;
        }
        if (!canSave || !persistenceOrgId) return;
        // Mutual exclusion with the history pane, in the other direction below.
        if (historyOpen) closeHistoryDrawer();
        dispatch(setShowCommentsThread({ source: COMMENTS_THREAD_SOURCES.REPORTS, assesseeOrgId: persistenceOrgId }));
    }, [commentsActive, canSave, persistenceOrgId, dispatch, historyOpen, closeHistoryDrawer]);

    const handleHistoryToggle = useCallback(() => {
        if (commentsActive) dispatch(closeCommentsThread());
        toggleHistoryDrawer();
    }, [commentsActive, dispatch, toggleHistoryDrawer]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        return () => {
            if (canSave) dispatch(resetAndHideCommentsThread());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { fitColumnsToolbarItem } = useColumnAutofit({ workbookRef, wrapperRef, enabled: !loading && !error && !!sheets?.length });
    const customToolbarItems = useMemo(() => [fitColumnsToolbarItem], [fitColumnsToolbarItem]);

    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(downloadMenuRef, () => setDownloadMenuOpen(false));

    const [renameOpen, setRenameOpen] = useState(false);

    const saveButtonLabel = SAVE_PHASE_LABELS[savingPhase];

    const headerRight =
        !loading && !error ? (
            <div className={styles.actions}>
                {canSave && autosaveStatus !== 'idle' && (
                    <div className={styles.headerSyncStatus} aria-live="polite">
                        {autosaveStatus === 'saving' ? (
                            <>
                                <SyncRoundedIcon className={clsx(styles.syncIcon, styles.syncIconSpin)} fontSize="small" />
                                <span>Saving</span>
                            </>
                        ) : (
                            <>
                                <svg viewBox="-1 -1 26 26" className={styles.syncIcon} aria-hidden fill="none">
                                    <path
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
                                    />
                                    <path fill="currentColor" d="M10 14.18L7.91 12.09L6.5 13.5L10 17L16.01 10.99L14.60 9.58Z" />
                                </svg>
                                <span>{autosaveStatus === 'error' ? 'Save failed' : 'Saved'}</span>
                            </>
                        )}
                    </div>
                )}
                {canSave && (
                    <button
                        type="button"
                        className={clsx(styles.historyBtn, commentsActive && styles.historyBtnActive)}
                        aria-label="Toggle comments"
                        title="Comments"
                        onClick={toggleComments}
                        disabled={!persistenceOrgId}
                    >
                        <img src={commentsIcon} alt="" aria-hidden className={styles.btnIcon} />
                    </button>
                )}
                {canSave && (
                    <button
                        type="button"
                        className={clsx(styles.historyBtn, historyOpen && styles.historyBtnActive)}
                        aria-label="View history"
                        title="Version history"
                        onClick={handleHistoryToggle}
                        disabled={!hasGenId}
                    >
                        <img src={ClockCounterDark} alt="" aria-hidden className={styles.btnIcon} />
                    </button>
                )}
                <div className={styles.downloadGroup} ref={downloadMenuRef}>
                    {isMobile ? (
                        <CompactDownloadButton onClick={() => setDownloadMenuOpen((o) => !o)} expanded={downloadMenuOpen} />
                    ) : (
                        <button
                            type="button"
                            className={styles.downloadBtn}
                            onClick={() => setDownloadMenuOpen((o) => !o)}
                            aria-haspopup="true"
                            aria-expanded={downloadMenuOpen}
                        >
                            Download <span className={styles.downloadCaret}>▾</span>
                        </button>
                    )}
                    {downloadMenuOpen && (
                        <div className={styles.downloadPopover} role="menu" aria-label="Download options">
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.downloadPopoverItem}
                                onClick={() => {
                                    setDownloadMenuOpen(false);
                                    handleDownloadXlsx();
                                }}
                            >
                                Download as .xlsx
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.downloadPopoverItem}
                                onClick={() => {
                                    setDownloadMenuOpen(false);
                                    handleDownloadCsv();
                                }}
                            >
                                Download as .csv
                            </button>
                        </div>
                    )}
                </div>
                {!viewOnly && onAddToReports ? (
                    <Button
                        type="button"
                        text={addedToReports ? <WhiteCheckIcon /> : 'Add to Workspace'}
                        variant="purple"
                        className={styles.headerBtn}
                        onClick={addedToReports ? () => {} : onAddToReports}
                        disabled={addingToReports}
                        loading={!addedToReports && addingToReports}
                    />
                ) : (
                    canSave && (
                        <Button
                            type="button"
                            text={saveButtonLabel}
                            variant="purple"
                            className={styles.headerBtn}
                            onClick={async () => {
                                // Close only on a confirmed save — a failed save keeps the
                                // drawer (and the user's unsaved edits) open.
                                if (await handleSave()) onClose();
                            }}
                            disabled={saving || historyOpen}
                            loading={saving}
                        />
                    )
                )}
            </div>
        ) : null;

    // Use fileUrl as the workbook key on the fetch path; fall back to fileName
    // so in-memory sheets get a stable key that doesn't remount on every render.
    // A history preview (or a restore, even without a prior preview) forces a
    // remount — fortune-sheet only reads `data` at init. Keyed on previewActive
    // (content actually swapped), NOT previewEntryId: opening the pane
    // auto-selects the latest row without changing content, and remounting
    // read-only there is both a large-workbook stall and the crash-prone path
    // (see handleRestoreVersion's comment on the nested-update loop).
    const workbookKey = `${fileUrl ?? fileName}::${previewActive ? previewEntryId : 'live'}::${restoreVersion}`;

    // Report save-context (Reports table open/import) gets an editable title —
    // same "name + purple Edit pill" markup PlateDocumentDrawer uses for Reports.
    // Other contexts (Data Room, Ask AICA) keep the plain file name, view-only.
    const displayName = currentName || fileName;
    const drawerTitle = canSave ? (
        <div className={styles.headerLeft}>
            <div className={styles.titleBlock}>
                <span className={styles.titleText} title={displayName}>
                    {displayName}
                </span>
                <div
                    className={styles.editPill}
                    onClick={() => setRenameOpen(true)}
                    role="button"
                    tabIndex={0}
                    aria-label="Edit name and description"
                    title="Edit"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setRenameOpen(true);
                        }
                    }}
                >
                    <img src={editIconPurple} alt="" aria-hidden className={styles.editPillIcon} />
                    <span>Edit</span>
                </div>
            </div>
        </div>
    ) : (
        fileName
    );

    // `preparing` (import: file still being parsed) holds the loader up even
    // once useResolveSheets has already settled to its empty-fallback blank
    // sheet — the caller hands sheets in once parsing finishes.
    const effectiveLoading = loading || Boolean(preparing);
    const loadingStatusText = preparing
        ? 'Preparing spreadsheet…'
        : loadingStage === 'downloading'
        ? 'Downloading file…'
        : loadingStage === 'parsing'
        ? 'Preparing spreadsheet…'
        : null;

    // workbookSheets already reflects whichever content is showing — a history
    // preview (read-only) or the live sheets — both lazy-hydrated the same way.
    const dataForWorkbook = workbookSheets;

    // Keeps the "Preparing spreadsheet…" overlay up through the Workbook's
    // first commit + first paint (not just until sheets resolve) — a large
    // workbook still blocks for a beat mounting/painting after `data` is ready.
    // Re-arms on every remount (workbookKey change: initial load, import, or a
    // history preview/restore swap) so each one gets the same brief cover.
    const [paintedKey, setPaintedKey] = useState<string | null>(null);
    useEffect(() => {
        if (effectiveLoading || error || !dataForWorkbook) return undefined;
        let raf1 = 0;
        let raf2 = 0;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => setPaintedKey(workbookKey));
        });
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [effectiveLoading, error, dataForWorkbook, workbookKey]);
    const showPaintOverlay = !effectiveLoading && !error && !!dataForWorkbook && paintedKey !== workbookKey;

    const workbookMounted = !effectiveLoading && !error && !!dataForWorkbook;
    useSheetTabWheelScroll(wrapperRef, workbookKey, workbookMounted);
    useWorkbookResizeSync(wrapperRef, workbookMounted);

    return (
        <>
            <BottomDrawer
                open={open}
                onClose={onClose}
                title={drawerTitle}
                headerRight={headerRight}
                className={styles.drawer}
                bodyClassName={styles.body}
                height="100vh"
                rightSlot={
                    canSave ? (
                        <>
                            <aside className={clsx(styles.historyPane, historyOpen && styles.historyPaneOpen)} aria-hidden={!historyOpen}>
                                <div id={HISTORY_PORTAL_TARGET_ID} className={styles.historyPortalTarget} />
                            </aside>
                            <aside
                                className={clsx(
                                    styles.commentsPane,
                                    commentsActive && styles.commentsPaneOpen,
                                    commentsExpanded && styles.commentsPaneExpanded,
                                )}
                                aria-hidden={!commentsActive}
                            >
                                <div id={COMMENTS_PORTAL_TARGET_ID} className={styles.commentsPortalTarget} />
                            </aside>
                        </>
                    ) : undefined
                }
            >
                {effectiveLoading && (
                    <div className={styles.statusBox}>
                        <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                        {loadingStatusText && <p className={styles.statusLine}>{loadingStatusText}</p>}
                    </div>
                )}
                {error && (
                    <div className={styles.statusBox}>
                        <p>Unable to open this file for editing.</p>
                    </div>
                )}
                {!effectiveLoading && !error && dataForWorkbook && (
                    <div className={styles.workbookWrapper} ref={wrapperRef}>
                        {showPaintOverlay && (
                            <div className={styles.paintOverlay}>
                                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                                <p className={styles.statusLine}>Preparing spreadsheet…</p>
                            </div>
                        )}
                        <SheetWorkbook
                            key={workbookKey}
                            data={dataForWorkbook}
                            workbookRef={workbookRef}
                            customToolbarItems={customToolbarItems}
                            allowEdit={!historyOpen && !viewOnly}
                            showToolbar={!viewOnly}
                            onOp={markWorkbookDirty}
                        />
                    </div>
                )}
            </BottomDrawer>
            {canSave && (
                <HistorySideDrawer
                    containerId={HISTORY_PORTAL_TARGET_ID}
                    isOpen={historyOpen}
                    isExpanded={historyExpanded}
                    title="Version history"
                    loading={historyLoading}
                    historyEntries={historyEntries}
                    onClose={closeHistoryDrawer}
                    onToggleExpand={toggleHistoryExpand}
                    onSelectEntry={selectHistoryEntry}
                    onPreviewEntry={previewHistoryEntry}
                    selectedEntryId={previewEntryId}
                    previewLoading={historyPreviewLoading}
                    restoreInFooter
                />
            )}
            {canSave && (
                <RenameReportDialog
                    open={renameOpen}
                    name={displayName}
                    description={currentDescription ?? ''}
                    onClose={() => setRenameOpen(false)}
                    onSave={(newName, newDescription) => {
                        setRenameOpen(false);
                        handleRename(newName, newDescription);
                    }}
                />
            )}
        </>
    );
};

export default SheetEditorDrawer;
